/**
 * FBA stockout audit (Isaac, 2026-10-13): pure so it is unit-testable without
 * Amazon or a database.
 *
 * Born from a missed restock. The question it answers is "how often did each
 * FBA SKU sit at zero sellable units, and how long after that did anyone
 * create the next shipment?" -- an objective record of whether restocks are
 * being planned or merely reacted to. It reads Amazon's daily inventory
 * ledger, so it does not depend on this app's order history (which only
 * starts 2026-05-28 and has gaps when the order sync is not run).
 */

/** A SKU must sell at least this much, for at least `MIN_ACTIVE_UNITS` units
 * in the window, to be audited -- the slow tail would otherwise bury the list. */
export const MIN_ACTIVE_UNITS_PER_DAY = 0.3;
export const MIN_ACTIVE_UNITS = 20;
/** A stockout shorter than this is ledger noise (an unlucky end-of-day count). */
export const MIN_STOCKOUT_DAYS = 3;

const DAY_MS = 864e5;

/** One SELLABLE ledger row: the ending balance for a SKU on a day. The ledger
 * only has a row for a SKU on days something happened or stock existed. */
export interface LedgerDay {
  sku: string;
  asin: string | null;
  title: string | null;
  /** UTC midnight, ms. */
  date: number;
  ending: number;
  /** Units shipped to customers that day (positive). */
  sold: number;
}

export interface AuditShipment {
  id: string;
  status: string;
  /** YYYY-MM-DD the shipment was created, parsed from its name. */
  createdOn: string;
  qty: number;
}

export interface StockoutPeriod {
  start: string;
  end: string;
  days: number;
  /** Days from the stockout starting to the next shipment being CREATED;
   * negative/0 = a shipment already existed. null = none created since. */
  nextShipmentAfterDays: number | null;
  /** Latest shipment created on/before the stockout, if any. */
  previousShipment: { createdOn: string; qty: number; daysBefore: number } | null;
}

export interface SkuAudit {
  sku: string;
  asin: string | null;
  title: string | null;
  unitsSold: number;
  /** Units/day on the days it WAS in stock -- the demand a restock should cover. */
  ratePerDay: number;
  daysOut: number;
  /** daysOut (stockouts of 3+ days) x ratePerDay, rounded. An estimate, not an Amazon figure. */
  estLostUnits: number;
  endingBalance: number;
  periods: StockoutPeriod[];
  shipments: AuditShipment[];
}

const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** "FBA STA (07/15/2026 15:00)-BNA6" -> "2026-07-15". Null when the name was
 * edited and no longer carries the creation date. */
export function parseShipmentCreatedDate(name: string | null | undefined): string | null {
  const m = /\((\d{2})\/(\d{2})\/(\d{4})/.exec(name ?? "");
  return m ? `${m[3]}-${m[1]}-${m[2]}` : null;
}

/** Stockout analysis from daily ledger rows over [startMs, endMs] (UTC days). */
export function analyzeLedger(rows: LedgerDay[], startMs: number, endMs: number): SkuAudit[] {
  const bySku = new Map<string, LedgerDay[]>();
  for (const r of rows) {
    const list = bySku.get(r.sku);
    if (list) list.push(r);
    else bySku.set(r.sku, [r]);
  }

  const out: SkuAudit[] = [];
  for (const [sku, list] of bySku) {
    const byDay = new Map(list.map((r) => [r.date, r]));
    let balance: number | null = null;
    let unitsSold = 0;
    let soldInStock = 0;
    let inDays = 0;
    const runs: { s: number; e: number }[] = [];
    let cur: { s: number; e: number } | null = null;

    for (let t = startMs; t <= endMs; t += DAY_MS) {
      const r = byDay.get(t);
      if (r) {
        balance = r.ending;
        unitsSold += r.sold;
      }
      // Before the SKU's first ledger row its balance is unknown, not zero.
      if (balance === null) continue;
      if (balance <= 0) {
        if (cur) cur.e = t;
        else cur = { s: t, e: t };
      } else {
        inDays++;
        if (r) soldInStock += r.sold;
        if (cur) {
          runs.push(cur);
          cur = null;
        }
      }
    }
    if (cur) runs.push(cur);
    if (balance === null) continue;

    const windowDays = Math.round((endMs - startMs) / DAY_MS) + 1;
    if (unitsSold < MIN_ACTIVE_UNITS || unitsSold / windowDays < MIN_ACTIVE_UNITS_PER_DAY) continue;

    const ratePerDay = inDays > 0 ? soldInStock / inDays : 0;
    const first = list[0];
    const periods = runs
      .filter((r) => (r.e - r.s) / DAY_MS + 1 >= MIN_STOCKOUT_DAYS)
      .map((r) => ({
        start: iso(r.s),
        end: iso(r.e),
        days: (r.e - r.s) / DAY_MS + 1,
        nextShipmentAfterDays: null,
        previousShipment: null,
      }));
    const periodDays = periods.reduce((n, p) => n + p.days, 0);
    out.push({
      sku,
      asin: first?.asin ?? null,
      title: first?.title ?? null,
      unitsSold,
      ratePerDay: Math.round(ratePerDay * 100) / 100,
      daysOut: periodDays,
      estLostUnits: Math.round(periodDays * ratePerDay),
      endingBalance: balance,
      periods,
      shipments: [],
    });
  }
  return out;
}

/** Adds each SKU's shipments and, per stockout, how soon a shipment followed. */
export function attachShipments(
  audits: SkuAudit[],
  shipmentsBySku: Map<string, AuditShipment[]>,
): SkuAudit[] {
  return audits.map((a) => {
    const shipments = [...(shipmentsBySku.get(a.sku) ?? [])].sort((x, y) =>
      x.createdOn.localeCompare(y.createdOn),
    );
    const periods = a.periods.map((p) => {
      const startMs = Date.parse(p.start);
      const prev = [...shipments].reverse().find((s) => Date.parse(s.createdOn) <= startMs);
      const next = shipments.find((s) => Date.parse(s.createdOn) >= startMs);
      return {
        ...p,
        nextShipmentAfterDays: next
          ? Math.round((Date.parse(next.createdOn) - startMs) / DAY_MS)
          : null,
        previousShipment: prev
          ? {
              createdOn: prev.createdOn,
              qty: prev.qty,
              daysBefore: Math.round((startMs - Date.parse(prev.createdOn)) / DAY_MS),
            }
          : null,
      };
    });
    return { ...a, shipments, periods };
  });
}

export interface AuditSummary {
  activeSkus: number;
  skusWithStockout: number;
  pctWithStockout: number;
  stockoutPeriods: number;
  daysOut: number;
  estLostUnits: number;
  currentlyOut: number;
  /** Stockouts where no shipment had been created by the time stock hit zero. */
  reactivePeriods: number;
  medianDaysToNextShipment: number | null;
  medianDaysSincePreviousShipment: number | null;
  /** Active SKUs with no more than one shipment in the window. */
  skusWithOneOrFewerShipments: number;
}

const median = (xs: number[]): number | null => {
  if (xs.length === 0) return null;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)] ?? null;
};

/** A shipment created within this many days before a stockout counts as
 * "planned" -- it was already on its way, just not soon enough. */
const PLANNED_WINDOW_DAYS = 45;

/** Reactive = nobody had created a shipment in the run-up; the next one only
 * followed (or never did) after the SKU was already at zero. */
function isReactive(p: StockoutPeriod): boolean {
  return !(p.previousShipment && p.previousShipment.daysBefore <= PLANNED_WINDOW_DAYS);
}

export function summarizeAudit(audits: SkuAudit[]): AuditSummary {
  const withOut = audits.filter((a) => a.periods.length > 0);
  const periods = audits.flatMap((a) => a.periods);
  return {
    activeSkus: audits.length,
    skusWithStockout: withOut.length,
    pctWithStockout: audits.length ? Math.round((withOut.length / audits.length) * 100) : 0,
    stockoutPeriods: periods.length,
    daysOut: periods.reduce((n, p) => n + p.days, 0),
    estLostUnits: withOut.reduce((n, a) => n + a.estLostUnits, 0),
    currentlyOut: audits.filter((a) => a.endingBalance <= 0).length,
    reactivePeriods: periods.filter(isReactive).length,
    medianDaysToNextShipment: median(
      periods.map((p) => p.nextShipmentAfterDays).filter((n): n is number => n !== null),
    ),
    medianDaysSincePreviousShipment: median(
      periods
        .map((p) => p.previousShipment?.daysBefore)
        .filter((n): n is number => n !== undefined),
    ),
    skusWithOneOrFewerShipments: audits.filter((a) => a.shipments.length <= 1).length,
  };
}
